import dayjs from 'dayjs';
import { getUser } from '@frontend/auth-helpers';
import { FREE_TRIAL_NUDGE_LOCAL_STORAGE_KEY } from './constants';

type FreeTrialNudgeStorage = {
  dismissCount: number; // how many times user dismissed in current 10-day window
  lastShown: dayjs.Dayjs; // timestamp of the last time banner was shown
  isUserInteracted: boolean; // indicates whether a user is interacted to the Nudge or not
};

type UserInteraction = 'DISMISS' | 'USER_INTERACTION';

// Indicates number of dismiss events allowed in a session between showing the free trial banner
const totalDismissCountAllowedInASession = 1;

/**
 * Determines whether the free trial nudge  should be shown
 */
export function shouldShowFreeTrialNudge(): boolean {
  const userId = getUser()?.userID || '';
  if (!userId) return false;

  const now = dayjs();
  const key = `${FREE_TRIAL_NUDGE_LOCAL_STORAGE_KEY}_${userId}`;
  const stored = localStorage.getItem(key);

  const END_DATE = dayjs('2026-04-30'); // After April 2026 there are no free trials

  if (now.isAfter(END_DATE)) {
    return false;
  }

  if (stored) {
    const data = JSON.parse(stored) as FreeTrialNudgeStorage;
    const lastShown = dayjs(data.lastShown);

    // Reset dismiss count if 10-day window has passed since lastShown
    if (now.diff(lastShown, 'day') >= 10) {
      data.dismissCount = 0;
      data.lastShown = now;
      data.isUserInteracted = false;
      localStorage.setItem(key, JSON.stringify(data));
      return true;
    }

    return data.dismissCount < totalDismissCountAllowedInASession && !data.isUserInteracted;
  } else {
    const intializeLocalStorageData: FreeTrialNudgeStorage = {
      dismissCount: 0,
      lastShown: now,
      isUserInteracted: false,
    };
    localStorage.setItem(key, JSON.stringify(intializeLocalStorageData));
    return true;
  }
}

/**
 * Call this function when user dismisses the tooltip
 */
export function recordFreetrialNudgeUserInteraction(type: UserInteraction) {
  const userId = getUser()?.userID || '';
  const key = `${FREE_TRIAL_NUDGE_LOCAL_STORAGE_KEY}_${userId}`;
  const stored = localStorage.getItem(key);
  if (!stored) return;

  const data: FreeTrialNudgeStorage = JSON.parse(stored);
  data.lastShown = dayjs();

  // Capture Dismiss Event
  if (type === 'DISMISS') {
    data.dismissCount += 1;
  }

  // Capture onClick events in the banner indicating a user is interacted
  if (type === 'USER_INTERACTION') {
    data.isUserInteracted = true;
  }

  localStorage.setItem(key, JSON.stringify(data));
}
